/**
 * Admin -> Edge Fleet -> Install tokens
 *
 * Command Plane Phase 3 step 4. Until this existed there was no way to see a
 * token at all: the only surface was minting one. For a single token that is
 * survivable - it binds to one pod and you can see the pod. For a FLEET token
 * it is not, because the thing you need to know is a number that lives only on
 * the token: how much of the ceiling is spent. Without it an operator learns
 * the fleet is full when devices start failing to enrol.
 *
 * TWO NUMBERS, NOT ONE. `enrolledCount` is what the token has SPENT and only
 * ever goes up - recycling slots on decommission would let a fleet quietly
 * exceed its licence. `livePods` is what is running now. They diverge as soon
 * as a device is retired, and an operator reading one as the other will
 * mis-size the next rollout, so both are shown with their own labels.
 *
 * REVOKE AND SEAL ARE DELIBERATELY NOT THE SAME BUTTON. Revoke stops every
 * device on the token; seal stops new ones joining and leaves the running fleet
 * alone. On a hundred machines that difference is the whole ballgame, so revoke
 * requires a typed reason and states the number of devices it is about to stop
 * BEFORE it happens.
 *
 * Backend:
 *   GET  /v1/edge/install-tokens
 *   POST /v1/edge/install-tokens/:id/revoke   { reason }
 *   POST /v1/edge/install-tokens/:id/seal
 */
import { useCallback, useEffect, useState } from 'react';
import { KeyRound, Loader2, Lock, RefreshCw, XCircle } from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  listEdgeInstallTokens,
  revokeEdgeInstallToken,
  sealEdgeInstallToken,
  type EdgeInstallTokenRow,
} from '@/lib/api';

/**
 * A token is shut in three different ways and an operator needs to tell them
 * apart: revoked is deliberate, expired is the clock, sealed is full on
 * purpose. Rendering them all as one grey "inactive" chip is how a revoked
 * token gets mistaken for a lapsed one.
 */
function tokenState(t: EdgeInstallTokenRow): { label: string; tone: string; hint: string } {
  if (t.revokedAt) {
    return {
      label: 'Revoked',
      tone: 'bg-red-500/15 text-red-300 border-red-500/30',
      hint: t.revocationReason ?? 'revoked',
    };
  }
  if (new Date(t.expiresAt).getTime() < Date.now()) {
    return {
      label: 'Expired',
      tone: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
      // Not a dead token. Expiry gates ENROLMENT, not operation - devices
      // already enrolled keep running on it by design.
      hint: 'no new devices; enrolled devices keep running',
    };
  }
  if (t.kind === 'fleet' && t.maxDevices !== null && t.enrolledCount >= t.maxDevices) {
    return {
      label: 'Full',
      tone: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
      hint: 'ceiling reached; the next device will be refused',
    };
  }
  return {
    label: 'Active',
    tone: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    hint: `expires ${new Date(t.expiresAt).toLocaleDateString()}`,
  };
}

export function InstallTokenPanel() {
  const [tokens, setTokens] = useState<EdgeInstallTokenRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await listEdgeInstallTokens();
      setTokens(r.tokens);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'could not load install tokens');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onRevoke = async (t: EdgeInstallTokenRow) => {
    // The count comes from the row the operator is looking at, and it is in the
    // prompt itself. "Revoke this token?" does not convey that 100 laptops stop.
    const what =
      t.livePods === 0
        ? 'No devices are currently running on this token.'
        : `This will STOP ${t.livePods} running device${t.livePods === 1 ? '' : 's'} at their next heartbeat.`;
    const reason = window.prompt(`${what}\n\nReason (recorded in the audit chain):`);
    if (reason === null) return; // cancelled
    if (reason.trim().length < 3) {
      setNotice('Revoke needs a reason of at least 3 characters.');
      return;
    }
    setBusyId(t.id);
    setNotice(null);
    try {
      const r = await revokeEdgeInstallToken(t.id, reason.trim());
      setNotice(
        r.livePodsStopped.length > 0
          ? `Revoked. Stopped: ${r.livePodsStopped.join(', ')}`
          : 'Revoked. No running devices were affected.',
      );
      await load();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : 'revoke failed');
    } finally {
      setBusyId(null);
    }
  };

  const onSeal = async (t: EdgeInstallTokenRow) => {
    setBusyId(t.id);
    setNotice(null);
    try {
      const r = await sealEdgeInstallToken(t.id);
      setNotice(
        r.sealed
          ? `Sealed at ${r.atDevices} device${r.atDevices === 1 ? '' : 's'}. Running devices are unaffected.`
          : 'Could not seal this token.',
      );
      await load();
    } catch (e) {
      // The 409 body explains WHY (an unused fleet token cannot be sealed -
      // that is just revoking it). Showing it beats a generic failure.
      setNotice(e instanceof Error ? e.message : 'seal failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <GlassCard className="p-0">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-4 w-4" />
            Install tokens
          </CardTitle>
          <CardDescription>
            A fleet token enrols many devices from one package. Seal stops new devices joining;
            revoke stops the ones already running.
          </CardDescription>
        </div>
        <Button variant="ghost" size="sm" onClick={() => void load()}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </CardHeader>

      <div className="px-6 pb-6">
        {error ? (
          <p className="text-sm text-red-300">{error}</p>
        ) : tokens === null ? (
          <p className="flex items-center gap-2 text-sm text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading tokens...
          </p>
        ) : tokens.length === 0 ? (
          <p className="text-sm text-slate-400">
            No install tokens yet. Mint one from Edge provisioning.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-400">
                  <th className="py-2 pr-4">Token</th>
                  <th className="py-2 pr-4">Type</th>
                  <th className="py-2 pr-4">Enrolled</th>
                  <th className="py-2 pr-4">Running now</th>
                  <th className="py-2 pr-4">State</th>
                  <th className="py-2 pr-4" />
                </tr>
              </thead>
              <tbody>
                {tokens.map((t) => {
                  const st = tokenState(t);
                  const shut = t.revokedAt !== null;
                  return (
                    <tr key={t.id} className="border-t border-white/5">
                      {/* Only the id. The token itself is shown once at mint and
                          never again - not even its hash leaves the server. */}
                      <td className="py-2 pr-4 font-mono text-xs text-slate-300">
                        {t.id.slice(0, 8)}
                      </td>
                      <td className="py-2 pr-4">
                        <Badge variant="outline">{t.kind}</Badge>
                      </td>
                      <td className="py-2 pr-4 tabular-nums">
                        {t.kind === 'fleet' && t.maxDevices !== null
                          ? `${t.enrolledCount} / ${t.maxDevices}`
                          : t.enrolledCount > 0 || t.boundPodId
                            ? '1 / 1'
                            : '0 / 1'}
                      </td>
                      <td className="py-2 pr-4 tabular-nums text-slate-300">{t.livePods}</td>
                      <td className="py-2 pr-4">
                        <span className={`rounded border px-2 py-0.5 text-xs ${st.tone}`}>
                          {st.label}
                        </span>
                        <span className="ml-2 text-xs text-slate-500">{st.hint}</span>
                      </td>
                      <td className="py-2 pr-4 text-right">
                        {shut ? null : (
                          <div className="flex justify-end gap-2">
                            {t.kind === 'fleet' && t.enrolledCount > 0 ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={busyId === t.id}
                                onClick={() => void onSeal(t)}
                              >
                                <Lock className="mr-1 h-3 w-3" /> Seal
                              </Button>
                            ) : null}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-300 hover:text-red-200"
                              disabled={busyId === t.id}
                              onClick={() => void onRevoke(t)}
                            >
                              <XCircle className="mr-1 h-3 w-3" /> Revoke
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {notice ? <p className="mt-3 text-sm text-slate-300">{notice}</p> : null}
      </div>
    </GlassCard>
  );
}
